import { Suspense, lazy } from 'react';
import { createRoot } from 'react-dom/client';
import App from './App';
import { PublicProfile } from './components/profile/PublicProfile';
import { PublicCertificate } from './components/certificate/PublicCertificate';
import { bootRuntime } from './engine/runtime';
import { setVmProvider } from './terminal';
import { LocaleProvider } from './state/useLocale';
// Imported for its side effect as much as for anything it exports: evaluating
// it settles which language this visit is in and writes `lang` and `dir` onto
// <html>, before a stylesheet has been applied or a component has rendered.
// A page that painted left-to-right and then turned around would be a visible
// flinch on every Arabic load.
import './lib/locale';
import { applyCountSwitch, noteArrival, noteFoundations, noteLanding, startSession, startPresence } from './lib/events';
import { personFromPath } from './lib/person';
import { certificateFromPath } from './lib/publicCertificate';
import { isBloborPath, retiredTasksTarget } from './lib/retired-tasks';
import { isAgentPath } from './lib/agent-keys';
import { wantsLanding } from './lib/landing';
import { trackFromPath, tracksVisible } from './lib/tracks';
import { isSupportPath } from './lib/desk';
import { isStudyPath, isTechniquesPath } from './lib/efficiency';
import { isMeasuredPath } from './lib/measured';
import { isDeskPath } from './lib/learner-desk';
import { Landing } from './components/home/Landing';
import { Foundations } from './components/foundations/Foundations';
import { Civil } from './components/civil/Civil';
import { Power } from './components/power/Power';
import { GridCourse } from './components/grid/GridCourse';
import { CourseDoor } from './components/PhoneGate';
import { AgentPage } from './components/agent/AgentPage';
import { BloborShowcase } from './components/blobor/BloborShowcase';
import './styles.css';

// Before anything reads the address: they take `?count=` and `?from=` off it
// (lib/events.ts). The switch goes first so a marked browser's arrival is not counted.
applyCountSwitch();
noteArrival();

/**
 * Whose public page this is, or null for every other address.
 *
 * Read here rather than inside App because the answer decides whether App runs
 * at all. `/u/<tag>` is a page about a learner, opened by anybody, and it must
 * not boot a virtual machine to draw three badges
 * (components/profile/PublicProfile.tsx).
 */
const person = personFromPath(window.location.pathname);

/**
 * Which certificate this page checks, or null for every other address.
 *
 * The same decision as the line above, for the same reader one step further
 * along: somebody holding a certificate id who wants to know whether it is
 * real (components/certificate/PublicCertificate.tsx). Checking one is a
 * single request, and must not cost a virtual machine.
 */
const certificate = certificateFromPath(window.location.pathname);

/**
 * The retired paid task board's addresses, and the two pages that replaced it.
 *
 * The owner retired paid tasks and AI credits on 2026-10-31. Blobor, our own
 * game, is a public showcase at `/blobor` now (components/blobor/), and the
 * board's "Use your own agent" section, the one part a learner still needs,
 * is its own page at `/agent` (components/agent/AgentPage.tsx). `/tasks`,
 * every address under it, and `/projects` (retired into `/tasks` on
 * 2026-10-18) are in old messages and on old pages, so they are kept as a
 * door rather than a dead end: the address becomes `/agent` for `#agent` and
 * `/blobor` for everything else before anything reads it, the way a hidden
 * track's becomes `/` below (lib/retired-tasks.ts holds the rule).
 *
 * Neither page needs a virtual machine. A reader who came to make a key or to
 * look at the game should not be made to download one, so both mount instead
 * of App.
 */
const retired = retiredTasksTarget(window.location.pathname, window.location.search, window.location.hash);
if (retired) window.history.replaceState(null, '', retired);
const agentPage = isAgentPath(window.location.pathname);
const blobor = isBloborPath(window.location.pathname);
const agentOrGame = agentPage || blobor;

/**
 * Whether this visit is the pitch rather than the course, and the third
 * instance of the same decision on this page.
 *
 * `App` renders nothing until a virtual machine is up — its whole tree is
 * gated on `missions`, which is set by `Promise.all([listMissions(),
 * bootRuntime()])` — so a stranger who typed the domain used to watch a boot
 * screen pull tens of megabytes before reading the first sentence of the
 * pitch. The landing page mounts instead of `App` for exactly the reason the
 * two branches above it do: somebody who has not asked for a course should not
 * be made to download one.
 *
 * It asks nothing about the browser. `/` used to be the pitch only for a
 * browser with no course record, and the ledger for one that had it — which
 * made the front page of the product invisible to everybody who had ever
 * opened a level. The ledger has its own address now (`/?view=course`, and
 * every "home" link in the app points at it), and the landing page carries
 * the returning learner itself: it reads the record after mounting and turns
 * its one call to action into their next level (components/home/Landing.tsx).
 * lib/landing.ts holds the rules and the reasoning.
 */
/**
 * Whether this is the staff support console, and the fourth instance of the
 * same decision on this page.
 *
 * `/support` is where the people who answer customer questions read them
 * (components/support/Desk.tsx, docs/support.md). The argument for keeping it
 * off `App` is the one made three times above and it is at its sharpest here:
 * a support agent has no use whatsoever for a virtual machine, and the whole
 * value of this page is that somebody can open it, read a question and answer
 * it in the time it currently takes the boot screen to finish.
 *
 * `wantsLanding` already answers false for any path that is not `/`, so
 * adding this to the chain is all that was needed — but it is added to
 * `landing`'s own guard as well, beside `person` and `agentOrGame`, because the
 * line reads as the list of things `/` is not and leaving one out of it is
 * how the next path-based screen gets this subtly wrong.
 *
 * The component is loaded lazily and it is the only branch here that is. The
 * console is several screens of interface that no learner will ever open, and
 * a static import would put the queue, the customer panel and the canned-reply
 * editor in the chunk every visitor downloads. The asset store answers every
 * path under `/support` with this same `index.html`
 * (`not_found_handling = "single-page-application"`), so there is no second
 * entry point to fight over path ownership with — the address is read here,
 * once, exactly as the three above it are.
 */
const desk = isSupportPath(window.location.pathname);

/**
 * The practice tracks — Foundations at `/foundations`, and the Civil and Power
 * and Energy previews at `/civil` and `/power` (docs/foundations.md) — the
 * same decision again: small in-page models, no virtual machine, so they
 * mount instead of App and are the parts of the product a phone can do.
 */
const addressedTrack = trackFromPath(window.location.pathname);
const practice = addressedTrack && tracksVisible(window.location.search) ? addressedTrack : null;
// A hidden track's address becomes the landing page (lib/tracks.ts TRACKS_PUBLIC).
if (addressedTrack && !practice) window.history.replaceState(null, '', '/');
const foundations = practice !== null;

const Desk = lazy(() => import('./components/support/Desk'));

/**
 * The efficiency pages (docs/efficiency-plan.md): the habits at `/efficiency`,
 * which the cost panel on a clear links to, and the study at `/study`, which
 * nothing links to until it opens with the cohorts. The same decision again:
 * text and one request, no virtual machine. Lazy, like the desk, because
 * almost no visit opens either.
 */
const study = isStudyPath(window.location.pathname);
const techniques = isTechniquesPath(window.location.pathname);
const Study = lazy(() => import('./components/efficiency/Study'));
const Techniques = lazy(() => import('./components/efficiency/Techniques'));
// "How we measured", the study behind the landing's savings card (lib/measured.ts).
const measured = isMeasuredPath(window.location.pathname);
const Measured = lazy(() => import('./components/efficiency/Measured'));
const efficiencyPage = study || techniques || measured;

/**
 * The learner's desk at `/desk` (docs/server-workspace.md §4), and the same
 * decision once more, with the strongest reason yet: the learner's machine
 * runs on our servers, so this page is the ticket, Check my work and what
 * the agent is doing, and must never load a virtual machine. Lazy, like the
 * pages above. Not the support console, which is `desk` above.
 */
const learnerDesk = isDeskPath(window.location.pathname);
const LearnerDesk = lazy(() => import('./components/learner-desk/LearnerDesk'));

const landing = !person && !certificate && !agentOrGame && !desk && !learnerDesk && !foundations && !efficiencyPage && wantsLanding(window.location.pathname, window.location.search);

/**
 * Where the terminal gets its machine.
 *
 * The runtime owns the one VM and cannot hand it to the terminal itself:
 * terminal.ts reaches the engine through api.ts, which is built on the
 * runtime, so a runtime that imported the terminal would close the cycle.
 * This module imports both and nothing imports it, which makes it the only
 * place the two can be introduced. `bootRuntime` is idempotent, so this
 * registers a lazy reference rather than starting a boot: the terminal awaits
 * the same boot the app screen is already showing.
 */
setVmProvider(async () => (await bootRuntime()).vm);

/**
 * Count this visit before anything can go wrong with it.
 *
 * Above `createRoot` on purpose: the most valuable visit in the table is the
 * one from a browser that is about to fail the support check and render an
 * apology instead of a product, and a `visit` recorded inside a component
 * would be the one visit that never got recorded. docs/analytics.md.
 *
 * Not on a public profile or certificate page, not on the agent page or the
 * Blobor showcase, and not on the landing page. `visit` is the top of the
 * funnel — visits, boots, levels — and somebody who opened a link to a
 * learner's record or certificate, or to the game, or to make an agent key,
 * or who is still reading what this is,
 * has not arrived at the course. Counting them there would not add a
 * measurement; it would quietly make every rate below it read worse, which is
 * the one way a funnel can lie without anybody editing a number.
 *
 * The landing page joining that list is what keeps `visit` meaning what it has
 * always meant, across the split: a reader who presses the one button on it
 * lands on `?mission=…`, which is `App`, which counts them here. The series
 * loses the visitors who never started, which it never distinguished anyway.
 *
 * Profile views, showcase views and now landing views are all worth counting, and
 * the vocabulary is closed on purpose (docs/analytics.md), so they wait for a
 * word of their own.
 *
 * The support console is excluded for a different reason, and the difference
 * is worth keeping straight. The three above are readers who have not arrived
 * at the course *yet*; an agent opening the desk is not a reader of this
 * product at all, and counting them would put the deployment's own staff into
 * the top of a funnel measuring how many strangers go on to boot a machine.
 * A handful of visits a day from two people is enough to move that rate.
 * There is no `desk` word either — the vocabulary is closed, a staff visit is
 * not a thing the funnel is for, and `support_staff.seen_at` already records
 * exactly when somebody was at the desk, for the one purpose that needs it.
 */
// The desk boots nothing, so a `visit` there would read as a boot that never came.
if (!person && !certificate && !agentOrGame && !desk && !learnerDesk && !landing && !foundations && !efficiencyPage) startSession();

/**
 * The landing page has a word of its own since 2026-10-02: `land`, with the
 * browser label and nothing else (lib/events.ts `noteLanding`). It is not a
 * `visit`, for every reason the paragraph above gives, so the course's rates
 * read exactly as they did; it is the row above them that says how many people
 * read the pitch before any of them opened the course.
 */
if (landing) noteLanding();
if (practice) noteFoundations(practice);

// Closed surface names only; the staff console is excluded.
if (!desk) startPresence(landing ? 'landing' : person || certificate || agentOrGame || foundations || efficiencyPage ? 'public' : 'course');

/**
 * StrictMode is deliberately off.
 *
 * Its development double-mount would run the boot effect twice against one
 * tab's virtual machine, stack two xterm instances per load, and create two
 * sessions. The components that own imperative resources clean up properly —
 * the boot aborts its first attempt — but paying an emulator start on every
 * reload makes the terminal genuinely harder to work on. Turn it back on when
 * auditing effect cleanup.
 */
createRoot(document.getElementById('root')!).render(
  <LocaleProvider>
    {person ? (
      <PublicProfile tag={person} />
    ) : certificate ? (
      <PublicCertificate id={certificate} />
    ) : agentPage ? (
      <AgentPage />
    ) : blobor ? (
      <BloborShowcase />
    ) : desk ? (
      /* No fallback element. The console draws its own quiet page while its
         first read of the queue is out (components/support/Desk.tsx), and a
         spinner from here would be a second loading state in front of it for
         however long the chunk takes on a fast connection — which is less
         time than it takes to read one. */
      <Suspense fallback={null}>
        <Desk />
      </Suspense>
    ) : learnerDesk ? (
      <Suspense fallback={null}>
        <LearnerDesk />
      </Suspense>
    ) : study ? (
      <Suspense fallback={null}>
        <Study />
      </Suspense>
    ) : techniques ? (
      <Suspense fallback={null}>
        <Techniques />
      </Suspense>
    ) : measured ? (
      <Suspense fallback={null}>
        <Measured />
      </Suspense>
    ) : practice === 'foundations' ? (
      <Foundations />
    ) : practice === 'civil' ? (
      <Civil />
    ) : practice === 'power' ? (
      <Power />
    ) : practice === 'grid' ? (
      <GridCourse />
    ) : landing ? (
      <Landing />
    ) : (
      /* A phone is offered the link for a computer before anything downloads
         (components/PhoneGate.tsx); everybody else goes straight in. */
      <CourseDoor>
        <App />
      </CourseDoor>
    )}
  </LocaleProvider>,
);
